import { useEffect, useRef } from 'preact/hooks';
import Plotly from 'plotly.js';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getChartTitleColor,
  resolveSeriesColor,
} from '../../themes/chartThemeColors';
import { normalizeCurrencyCode } from '../../functions/calculations/currencyNormalizer';
import {
  plotlyConfig,
  plotlyFontSizes,
  plotlyTransparentBg,
  stackedBarChartConfig,
} from './config';

export interface StackedBarData {
  label: string;
  values: number[];
  /** Theme color key (e.g. "primary") or explicit CSS color. */
  color?: string;
}

interface StackedBarChartProps {
  xLabels: string[];
  stacks: StackedBarData[];
  title?: string;
  height?: number | string;
  width?: number | string;
  className?: string;
  currencyCode?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  /** Max number of ticks to show on x-axis (avoids crowding when many dates) */
  maxXTicks?: number;
  loading?: boolean;
  tooltipPrefix?: string;
  /** 'stack' = stacked bars; 'relative' = relative/overlay; 'group' = clustered bars side-by-side */
  barmode?: 'stack' | 'relative' | 'group';
}

export const StackedBarChart = ({
  xLabels,
  stacks,
  title = '',
  height = stackedBarChartConfig.defaults.height,
  width = stackedBarChartConfig.defaults.width,
  className = '',
  currencyCode,
  yAxisLabel = '',
  xAxisLabel = '',
  maxXTicks,
  loading = false,
  tooltipPrefix = stackedBarChartConfig.defaults.tooltipPrefix,
  barmode = stackedBarChartConfig.barmode
}: StackedBarChartProps) => {
  const { theme } = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const cfg = stackedBarChartConfig;
  const sizes = plotlyFontSizes;
  const isUnitIncomeChart = className.includes('unit-income-chart');
  const isUnitBalanceChart = className.includes('unit-balance-chart');
  const isUnitOverviewChart = isUnitIncomeChart || isUnitBalanceChart;
  const isFsChartPanel = className.includes('fs-chart-panel');
  const chartMinHeight = isUnitOverviewChart || isFsChartPanel ? 0 : cfg.defaults.minHeight;

  useEffect(() => {
    if (!plotRef.current || !xLabels.length || !stacks.length) {
      return;
    }

    const normalizedCurrency = normalizeCurrencyCode(currencyCode);
    const gridColor = resolveSeriesColor(theme, cfg.colors.grid);
    const legendColor = resolveSeriesColor(theme, cfg.colors.legend);

    const n = xLabels.length;
    const xAxisTickConfig =
      maxXTicks != null && n > maxXTicks && n > 1
        ? (() => {
            const step = (n - 1) / (maxXTicks - 1);
            const tickvals: number[] = [];
            const ticktext: string[] = [];
            let lastIdx = -1;
            for (let i = 0; i < maxXTicks; i++) {
              const idx = Math.min(Math.round(i * step), n - 1);
              if (idx > lastIdx) {
                tickvals.push(idx);
                ticktext.push(xLabels[idx]);
                lastIdx = idx;
              }
            }
            return { tickmode: 'array' as const, tickvals, ticktext };
          })()
        : {};

    const plotData: Plotly.Data[] = stacks.map((stack, index) => {
      const colorRef = stack.color ?? cfg.defaultStackColors[index % cfg.defaultStackColors.length];
      const barColor = resolveSeriesColor(theme, colorRef);

      return {
        x: xLabels,
        y: stack.values,
        name: stack.label,
        type: 'bar',
        marker: {
          color: barColor,
          line: { width: cfg.marker.lineWidth }
        },
        width: cfg.barWidth,
        hovertemplate: `${tooltipPrefix}%{x} – ${stack.label}: ${normalizedCurrency ? normalizedCurrency + ' ' : ''}%{y:,.0f}<extra></extra>`
      };
    });

    const margins = isUnitIncomeChart
      ? { t: 30, r: 8, b: 28, l: 42 }
      : isUnitBalanceChart
        ? { t: 30, r: 10, b: 32, l: 48 }
      : title
        ? cfg.layout.margins.withTitle
        : cfg.layout.margins.withoutTitle;

    const layout: Partial<Plotly.Layout> = {
      title: title ? {
        text: title.toUpperCase(),
        font: {
          family: theme.typography.fontFamily,
          size: sizes.titleFontSize,
          color: getChartTitleColor(theme)
        },
        x: 0,
        xanchor: 'left',
        y: 1,
        yanchor: 'top'
      } : undefined,
      font: {
        family: theme.typography.fontFamily,
        color: resolveSeriesColor(theme, cfg.colors.text),
        size: sizes.legendFontSize
      },
      barmode,
      bargap: cfg.bargap,
      ...plotlyTransparentBg,
      margin: { ...margins },
      xaxis: {
        title: xAxisLabel,
        showgrid: cfg.layout.showXGrid,
        tickfont: { size: sizes.tickFontSize },
        ...xAxisTickConfig
      },
      yaxis: {
        title: yAxisLabel || normalizedCurrency || '',
        showgrid: cfg.layout.showYGrid,
        gridcolor: gridColor,
        tickformat: isUnitOverviewChart ? '~s' : cfg.layout.yTickformat,
        separatethousands: !isUnitOverviewChart,
      },
      legend: isUnitIncomeChart
        ? {
            orientation: 'h',
            y: 1.02,
            yanchor: 'bottom',
            x: 1,
            xanchor: 'right',
            font: { size: Math.max(sizes.tickFontSize - 1, 9), color: legendColor },
          }
        : isUnitBalanceChart
          ? {
              orientation: 'h',
              y: 1.02,
              yanchor: 'bottom',
              x: 1,
              xanchor: 'right',
              font: { size: Math.max(sizes.tickFontSize - 1, 9), color: legendColor },
            }
        : {
            orientation: cfg.legend.orientation,
            y: cfg.legend.y,
            x: cfg.legend.x,
            xanchor: cfg.legend.xanchor,
            font: { size: sizes.tickFontSize, color: legendColor },
          },
      autosize: true,
      width: typeof width === 'number' ? width : undefined,
      height: typeof height === 'number' ? height : undefined
    };

    Plotly.newPlot(plotRef.current, plotData, layout, plotlyConfig);

    const handleResize = () => {
      if (plotRef.current) {
        Plotly.Plots.resize(plotRef.current);
      }
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(plotRef.current);
    const parentElement = plotRef.current.parentElement;
    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    const timeoutId = window.setTimeout(handleResize, 150);

    return () => {
      window.clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (plotRef.current) {
        Plotly.purge(plotRef.current);
      }
    };
  }, [xLabels, stacks, title, height, width, currencyCode, theme, yAxisLabel, xAxisLabel, maxXTicks, barmode, tooltipPrefix, className, loading, isUnitOverviewChart]);

  if (loading) {
    return (
      <div className={className} style={{ width: '100%', height: '100%', minHeight: chartMinHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>{cfg.loadingMessage}</p>
      </div>
    );
  }

  if (!xLabels.length || !stacks.length) {
    return (
      <div className={`chart-empty-state ${className}`} style={{ minHeight: chartMinHeight }}>
        <p>{cfg.emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%', height: '100%', minHeight: chartMinHeight }}>
      <div ref={plotRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
